import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { dramas } from "@/mocks/dramas";
import { reviews } from "@/mocks/reviews";
import { specials } from "@/mocks/specials";
import HeroCarousel from "./components/HeroCarousel";
import DramaCard from "@/components/base/DramaCard";
import ReviewCard from "@/components/base/ReviewCard";
import SectionHeading from "@/components/feature/SectionHeading";

export default function Home() {
  useSeo({
    title: "出轨的妻子影视大全_剧情介绍与影评分析",
    description:
      "出轨的妻子网站提供多部相关影视作品的剧情介绍、角色分析和专家影评，涵盖电视剧和电影内容，帮助用户深入了解剧情发展和人物关系。",
    keywords: "出轨的妻子,剧情介绍,影视影评,电视剧推荐,电影解析,情感剧,角色分析",
  });

  const heroItems = useMemo(
    () => [...dramas].sort((a, b) => b.popularity - a.popularity).slice(0, 4),
    []
  );
  const latest = useMemo(
    () => [...dramas].sort((a, b) => b.year - a.year || b.popularity - a.popularity).slice(0, 6),
    []
  );
  const hotRanking = useMemo(
    () => [...dramas].sort((a, b) => b.popularity - a.popularity).slice(0, 6),
    []
  );
  const featuredReviews = useMemo(() => reviews.slice(0, 4), []);

  return (
    <div className="pb-4">
      <HeroCarousel items={heroItems} />

      {/* Category quick filter strip */}
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-3.5 flex items-center gap-3 overflow-x-auto">
          <span className="shrink-0 text-sm text-foreground-500 flex items-center gap-1.5">
            <i className="ri-price-tag-3-line" />
            热门标签
          </span>
          {["家庭伦理", "情感剧", "悬疑", "都市", "女性成长", "婚姻"].map((tag) => (
            <Link
              key={tag}
              to={`/category?tag=${encodeURIComponent(tag)}`}
              className="shrink-0 rounded-full border border-background-300 bg-background-50 px-3.5 py-1 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {tag}
            </Link>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 md:px-6 mt-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-12">
            <section>
              <SectionHeading
                title="最新影视推荐"
                subtitle="最近上线的「出轨的妻子」题材作品，含电影与电视剧"
                moreTo="/category"
              />
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-5">
                {latest.map((drama) => (
                  <DramaCard key={drama.id} drama={drama} />
                ))}
              </div>
            </section>

            <section>
              <SectionHeading
                title="影评精选"
                subtitle="来自影评人与影视研究的深度解读"
                moreTo="/special"
                moreLabel="进入影评专题"
              />
              <div className="space-y-4">
                {featuredReviews.map((review) => (
                  <ReviewCard key={review.id} review={review} layout="horizontal" />
                ))}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <aside className="space-y-10">
            <section className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3.5 border-b border-background-200 bg-secondary-50">
                <i className="ri-fire-line text-accent-500" />
                <h2 className="font-heading text-base font-semibold text-foreground-950">热门排行榜</h2>
              </div>
              <ol className="divide-y divide-background-100">
                {hotRanking.map((drama, index) => (
                  <li key={drama.id}>
                    <Link
                      to={`/detail/${drama.id}`}
                      className="group flex items-center gap-3 px-4 py-3 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <span
                        className={`w-6 text-center font-heading text-lg font-bold ${
                          index < 3 ? "text-accent-500" : "text-foreground-400"
                        }`}
                      >
                        {index + 1}
                      </span>
                      <div className="w-11 shrink-0 rounded overflow-hidden bg-background-200" style={{ height: "60px" }}>
                        <img
                          src={drama.poster}
                          alt={`${drama.title} 封面`}
                          title={`${drama.title}·出轨的妻子影视大全`}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
                          {drama.title}
                        </h3>
                        <p className="mt-0.5 text-xs text-foreground-500">
                          {drama.type} · {drama.year} · {drama.genre}
                        </p>
                      </div>
                      <span className="text-sm font-semibold text-accent-600 shrink-0">
                        {drama.rating.toFixed(1)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3.5 border-b border-background-200 bg-primary-50">
                <i className="ri-folder-video-line text-primary-500" />
                <h2 className="font-heading text-base font-semibold text-foreground-950">主题专题</h2>
              </div>
              <ul className="divide-y divide-background-100">
                {specials.map((special) => (
                  <li key={special.id}>
                    <Link
                      to="/special"
                      className="group block px-4 py-3.5 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
                        {special.title}
                      </h3>
                      <p className="mt-1 text-xs text-foreground-500">
                        {special.group} · {special.articles.length} 篇文章
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          </aside>
        </div>
      </div>

      {/* Specials full-width */}
      <section className="mt-16 bg-background-100 border-y border-background-200">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-12">
          <SectionHeading
            title="专辑策划"
            subtitle="围绕婚姻、信任与选择展开的主题专题，串联同题材影视作品"
            moreTo="/special"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {specials.slice(0, 4).map((special) => (
              <Link
                key={special.id}
                to="/special"
                className="group relative rounded-lg overflow-hidden h-52 cursor-pointer"
              >
                <img
                  src={special.cover}
                  alt={`${special.title} 专题配图`}
                  title={`${special.title}·影视专题`}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/10" />
                <div className="absolute inset-0 p-5 flex flex-col justify-end">
                  <span className="self-start rounded-full bg-accent-500 px-2.5 py-0.5 text-[11px] font-medium text-white mb-2">
                    {special.group}
                  </span>
                  <h3 className="font-heading text-lg font-bold text-white line-clamp-1">
                    {special.title}
                  </h3>
                  <p className="mt-1.5 text-xs leading-5 text-white/75 line-clamp-2">{special.intro}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Site intro */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-14">
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-10 grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">关于「出轨的妻子」影视资料站</h2>
            <p className="mt-4 text-sm md:text-base leading-8 text-foreground-700">
              我们专注梳理「出轨的妻子」题材影视作品，围绕婚姻、信任与情感选择，提供完整的剧情介绍、角色关系分析与专家影评。
              无论你想快速了解一部剧讲了什么，还是希望深入读懂人物的每一个选择，这里都能找到对应的解读。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/about"
                className="inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                了解网站定位
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                联系我们
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-3 lg:grid-cols-1 gap-4 lg:gap-5">
            {[
              { value: `${dramas.length}+`, label: "收录影视作品" },
              { value: `${reviews.length}+`, label: "专家影评文章" },
              { value: `${specials.length}`, label: "主题专题策划" },
            ].map((stat) => (
              <div key={stat.label} className="rounded-lg bg-secondary-50 border border-secondary-200 p-4 text-center lg:text-left">
                <p className="font-heading text-2xl font-bold text-primary-600">{stat.value}</p>
                <p className="mt-1 text-xs text-foreground-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}